import gql from 'graphql-tag';

import type { FeedAssetGroup } from 'src/data/project';
import { executeGqlQuery } from 'src/graphql/client';
import { getSingleProp, throwIfResultHasErrors } from 'src/lib/graphql';
import type { BackendQueryJobStatusArgs } from 'src/types/backend';

import { FRAGMENT_ASSET_GROUP, FRAGMENT_SHORT_FORM_ASSET_IMPROVE_RESULT } from './schema';

type Variables = Pick<BackendQueryJobStatusArgs, 'id'>;

// `Job.assetGroups` returns exactly the groups a generation job produced, so the feed can absorb
// them directly instead of re-reading the whole section. Deliberately not `resultObject` /
// `resultList`: those resolve to the legacy `JobResultTypes` union, which has no `AssetGroup`
// member. A group still generating comes back with negative-id dummy assets attached, which is
// what `isDummyVariationId` renders as skeletons.
const QUERY = gql`
  query JobAssetGroups($id: ID!) {
    jobStatus(id: $id) {
      id
      assetGroups {
        ...AssetGroupFields
        # The one query that asks what a run came back with, because it is the one that runs something.
        assets {
          ...ShortFormAssetImproveResult
        }
      }
    }
  }
  ${FRAGMENT_ASSET_GROUP}
  ${FRAGMENT_SHORT_FORM_ASSET_IMPROVE_RESULT}
`;

interface RawJobAssetGroups {
  id: string;
  assetGroups?: (FeedAssetGroup | null)[] | null;
}

export const queryJobAssetGroups = async (variables: Variables): Promise<FeedAssetGroup[]> => {
  const result = await executeGqlQuery({ query: QUERY, variables });
  throwIfResultHasErrors(result);

  const job = getSingleProp<RawJobAssetGroups | null>(result.data);

  return (job?.assetGroups ?? []).filter(Boolean) as FeedAssetGroup[];
};
